import { ErrorHandler, Injectable } from '@angular/core'

import { BankConsentLog } from '@lemon/core-firebase/model/banks'
import {
  EnableBankingAspspList,
  EnableBankingAspspSubtype,
  EnableBankingLemonaidOauthStartRequest,
  EnableBankingLemonaidOauthStartResponse,
  PSUType,
} from '@lemon/core-firebase/model/enable-banking-shared'
import { BankConsentUriService } from '@lemon/core-firebase/service/bank-consent-uri.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { Bic, BicService } from '@lemon/core/service/bic.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'
import { Timestamp } from 'firebase/firestore'
import { FirebaseLemonaid } from './firebase-lemonaid.service'

export interface ViewableEnableBankingAspspSubtype {
  name: string
  psuTypes: { psuType: PSUType; labelKey: string }[]
}

@Injectable()
export class EnableBankingDialogService {
  private _enableBankingFlowCompletedAt: Date = null
  private _postponeStarted: Date = null
  private _aspspCache: EnableBankingAspspList['bankSubtypes'] = null
  private _aspspInFlight: Promise<EnableBankingAspspList['bankSubtypes']> = null

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _bankConsentUriService: BankConsentUriService,
    private _bicService: BicService,
    private _errorHandler: ErrorHandler,
  ) {}

  setFlowCompleted() {
    this._enableBankingFlowCompletedAt = new Date()
  }

  flowCompletedRecently(): boolean {
    if (!this._enableBankingFlowCompletedAt) {
      return false
    }
    const oneHourInMilliseconds = 60 * 60 * 1000
    return new Date().getTime() - this._enableBankingFlowCompletedAt.getTime() < oneHourInMilliseconds
  }

  postpone() {
    this._postponeStarted = new Date()
  }

  postponedRecently(): boolean {
    if (!this._postponeStarted) {
      return false
    }
    const oneHourInMilliseconds = 60 * 60 * 1000
    return new Date().getTime() - this._postponeStarted.getTime() < oneHourInMilliseconds
  }

  async fetchEnableBankingOauthStartUrl(asiakasAvain: string, subtypeName: string, psuType: PSUType, kieli: TuettuKieli, iban?: string): Promise<string> {
    const data: EnableBankingLemonaidOauthStartRequest = {
      subtypeName: subtypeName,
      kieli: kieli,
      psuType: psuType,
      iban: iban,
    }

    if (environment.environment === EnvironmentType.BETA) {
      data.beta = true
    }

    const resp = await this._firebaseLemonaid.functionsCall<EnableBankingLemonaidOauthStartRequest, EnableBankingLemonaidOauthStartResponse>('enableBankingOauthConnectStart', data)
    if (!resp || resp.e) {
      await this.saveHistory(asiakasAvain, 'Enable Banking -valtuutuksen linkin luonti epäonnistui', resp?.e ?? 'unknown-error')
      throw new Error(resp ? resp.e : 'unknown-error')
    }
    await this.saveHistory(asiakasAvain, 'Enable Banking -valtuutuksen linkki luotu, asiakas ohjataan pankkiin', `${subtypeName} (${psuType})`)
    return resp.redirectUrl
  }

  async getAspspDisplayData(): Promise<EnableBankingAspspList['bankSubtypes']> {
    if (this._aspspCache && Object.keys(this._aspspCache).length > 0) {
      return this._aspspCache
    }
    // Memoize in-flight haku ettei rinnakkaiskutsut tuplaa Firestore-lukua.
    // Tyhjää tulosta ei cacheta — esim. deploymentin jälkeen ennen enableBankingCacheAspspList-funktion ajoa
    // dokumentti voi puuttua, jolloin seuraava kutsu yrittää uudelleen.
    if (!this._aspspInFlight) {
      this._aspspInFlight = this._firebaseLemonaid
        .firestoreDoc<EnableBankingAspspList>('integrations/enable-banking/aspsp-list/fi')
        .get()
        .then(data => {
          const bankSubtypes = data?.bankSubtypes || {}
          if (bankSubtypes && Object.keys(bankSubtypes).length > 0) {
            this._aspspCache = bankSubtypes
          }
          return bankSubtypes
        })
        .finally(() => {
          this._aspspInFlight = null
        })
    }
    return this._aspspInFlight
  }

  /**
   * Returns the translation key for a PSU type label.
   */
  labelKeyForPsuType(psuType: PSUType): string {
    if (psuType === PSUType.PERSONAL) {
      return 'general-bank-selection.yksityistili'
    }
    if (psuType === PSUType.BUSINESS) {
      return 'general-bank-selection.yritystili'
    }
    return psuType
  }

  saveHistory(asiakasAvain: string, changedReason: string, details: string) {
    const logData: BankConsentLog = {
      asiakasAvain: asiakasAvain,
      source: 'enable-banking',
      changedTime: Timestamp.now(),
      changedReason: changedReason,
      details: details ?? null,
    }
    const avain = this._firebaseLemonaid.firestoreCreateId()
    const uri = this._bankConsentUriService.getBankConsentLogUri(asiakasAvain, avain)

    return this._firebaseLemonaid.firestoreSetData(uri, logData).catch(err => this._errorHandler.handleError(new Error(`Enable Banking log save failed (${changedReason}): ${err?.message}`)))
  }

  annaEbAspspBic(bic: Bic): string {
    // Revolutin BIC on EB ASPSP datan mukaan REVOLT21, mutta IBANin BIC on REVOLT21XXX. Käytetään EB ASPSP datan mukaista BICiä.
    return bic === 'REVOLT21XXX' ? 'REVOLT21' : bic
  }

  getBankSubtypesForIban(iban: string, bankTypes: EnableBankingAspspList['bankSubtypes']) {
    return bankTypes?.[this.annaEbAspspBic(this._bicService.annaBicKoodi(iban))] ?? []
  }

  convertToSelectedEbSubtype(subtype: EnableBankingAspspSubtype): ViewableEnableBankingAspspSubtype {
    return {
      name: subtype.name,
      psuTypes: subtype.psuTypes.map(psuType => ({
        psuType,
        labelKey: this.labelKeyForPsuType(psuType),
      })),
    }
  }
}
